<template>
  <FreeBottomSheet :name="name">
    <template #default>
      <CashbackInfoBottomSheetShimmer v-if="cashback.isLoading.value" />
      <div v-else class="bg-background-neutral-primary space-y-4 p-4">
        <template v-if="shouldShowPromoBlock">
          <CashbackInfoPromoBlock
            :cashback-levels="cashbackLevels"
            :is-promo-applicable="cashback.isPromoActive.value"
          />
          <CashbackInfoPromoCountdown
            :promo-end-date="cashback.promoEndDate.value"
            :is-promo-applicable="cashback.isPromoActive.value"
            @onExpired="cashback.refetchCashbackInfo"
          />
        </template>
        <div
          v-else
          class="bg-background-neutral-secondary space-y-4 rounded-3xl px-4 pt-4 shadow-[0px_5px_10px_rgba(0,0,0,0.05)]"
        >
          <div class="space-y-1">
            <p class="text-text-neutral-secondary text-lg font-semibold">
              {{ t('cashbackInfo.your-cashback') }}
            </p>
            <p class="freedom-loyalty-text inline-block text-3xl font-bold">
              {{ cashback.cashbackPercent.value }}%
            </p>
          </div>
          <div class="border-situational-lightgrey border-t" />
          <CashbackInfoLevels :cashback-levels="cashbackLevels" />
        </div>

        <div
          class="bg-background-neutral-secondary space-y-4 rounded-3xl p-4 shadow-[0px_5px_20px_rgba(0,0,0,0.05)]"
        >
          <div class="space-y-2">
            <p class="text-text-neutral-tertiary text-xs font-medium">
              {{ t('cashbackInfo.monthly-limit') }}
            </p>
            <div class="flex items-baseline gap-1">
              <span class="text-text-neutral-secondary text-lg font-semibold">
                {{ formattedReceivedCashback }}
              </span>
              <span class="text-text-neutral-tertiary text-sm">/</span>
              <span class="text-text-neutral-tertiary text-sm">
                {{ formattedLimitPerMonth }}
              </span>
            </div>
          </div>
          <div
            class="bg-background-neutral-primary relative h-4 w-full rounded-full"
          >
            <div
              class="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#85FFAF] to-[#4EBC73]"
              :style="{
                width:
                  limitProgressPercent >= 5 ? `${limitProgressPercent}%` : '0%',
              }"
            />
            <div
              class="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-md"
              :style="{
                left:
                  limitProgressPercent >= 5
                    ? `calc(${limitProgressPercent}% - 8px)`
                    : '2%',
              }"
            />
          </div>
          <div class="border-situational-lightgrey border-t" />
          <div class="space-y-1">
            <p class="text-text-neutral-tertiary text-xs font-medium">
              {{ t('cashbackInfo.max-per-transaction-label') }}
            </p>
            <p class="text-text-neutral-secondary text-base font-semibold">
              {{ cashback.levelName.value }} —
              {{ formattedLimitPerTransaction }}
            </p>
          </div>
        </div>

        <div
          class="rounded-3xl bg-gradient-to-br from-[rgba(1,212,138,0.15)] to-[rgba(0,111,194,0.15)] p-4"
        >
          <p class="freedom-loyalty-text text-base font-medium">
            {{ priceWithCashbackText }}
          </p>
        </div>

        <CashbackInfoFaq :cashback-levels="cashbackLevels" />

        <FreeButton
          type="button"
          class="green-main-button w-full text-lg font-semibold"
          @click="bottomSheet.hide(name)"
        >
          {{ t('cashbackInfo.book') }}
        </FreeButton>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { prettyPrice } from '@aviata/text-fns';
  import { controllerModel } from 'shared';

  import { FreeBottomSheet, FreeButton } from '../../../shared/ui';
  import type { FreedomCashbackModel } from '../../../features/freedom-cashback/model';
  import { FlowType } from '../../../shared/config/flow-type';
  import { CASHBACK_LEVELS, TOURS_LIMIT_PER_TRANSACTION } from '../config';
  import CashbackInfoBottomSheetShimmer from './CashbackInfoBottomSheetShimmer.vue';
  import CashbackInfoFaq from './CashbackInfoFaq.vue';
  import CashbackInfoLevels from './CashbackInfoLevels.vue';
  import CashbackInfoPromoCountdown from './CashbackInfoPromoCountdown.vue';
  import CashbackInfoPromoBlock from './CashbackInfoPromoBlock.vue';
  import messages from '../locales.json';

  const props = defineProps<{
    name: string;
    cashback: FreedomCashbackModel;
    app: FlowType;
  }>();

  const { t } = useI18n({ messages });
  const bottomSheet = controllerModel.useBottomSheet();

  const cashbackLevels = computed(() =>
    CASHBACK_LEVELS.map(level => ({
      ...level,
      isActive: level.name === props.cashback.levelName.value,
    })),
  );

  const priceWithCashbackText = computed(() => {
    if (props.app === FlowType.TOURS) {
      return t('cashbackInfo.price-with-cashback-tours');
    }

    return t('cashbackInfo.price-with-cashback');
  });

  const formatAmount = (amount: number) => `${prettyPrice(String(amount))} ₸`;

  const isToursOrHotels = computed(
    () => props.app === FlowType.TOURS || props.app === FlowType.HOTELS,
  );

  const formattedReceivedCashback = computed(() => {
    const usedCashback = isToursOrHotels.value
      ? props.cashback.aviataUsedCashbackAmount.value
      : props.cashback.receivedCashbackSum.value;

    return formatAmount(usedCashback);
  });

  const formattedLimitPerMonth = computed(() =>
    formatAmount(props.cashback.limitPerMonth.value),
  );

  const formattedLimitPerTransaction = computed(() => {
    if (props.app === FlowType.TOURS) {
      return formatAmount(TOURS_LIMIT_PER_TRANSACTION);
    }

    return formatAmount(props.cashback.limitPerTransaction.value);
  });

  const limitProgressPercent = computed(() => {
    if (!props.cashback.limitPerMonth.value) {
      return 0;
    }

    const usedCashback = isToursOrHotels.value
      ? props.cashback.aviataUsedCashbackAmount.value
      : props.cashback.receivedCashbackSum.value;

    return Math.min(
      100,
      Math.round((usedCashback / props.cashback.limitPerMonth.value) * 100),
    );
  });

  const shouldShowPromoBlock = computed(() => {
    return (
      props.cashback.isUserApplicableForPromo.value ||
      props.cashback.isPromoActive.value
    );
  });
</script>
